<template>
  <div class="chat-input-area">
    <!-- デモモード -->
    <div
      v-if="isDemoMode"
      class="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed"
    >
      デモでは送信できません。無料登録後、ご自身のアカウントで質問できます。
    </div>

    <!-- 上限警告 -->
    <div
      v-if="isLimitReached && !isUnlimited"
      class="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl"
    >
      <div class="flex items-center">
        <svg class="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div class="text-sm">
          <div class="text-red-800 font-medium">今月の質問回数上限に達しました</div>
          <div class="text-red-600 text-xs mt-0.5">
            プランをアップグレードするか、来月までお待ちください。
            <a :href="planChangeUrl" class="underline hover:no-underline">プラン変更</a>
          </div>
        </div>
      </div>
    </div>

    <form
      class="flex items-end gap-3 p-4 pr-44 md:p-5 md:pr-5"
      @submit.prevent="handleSubmit"
    >
      <div class="flex-1 min-w-0">
        <textarea
          ref="textareaRef"
          v-model="inputValue"
          v-bind="textareaA11yAttrs"
          :disabled="disabled || isDemoMode || isLimitReached"
          :class="[
            'w-full px-5 py-4 rounded-xl border text-[15px] resize-none min-h-[52px] max-h-[140px] transition-all',
            'focus:outline-none focus:ring-2 focus:ring-[#37acdb] focus:border-transparent',
            (disabled || isDemoMode || isLimitReached)
              ? 'bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500'
              : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'
          ]"
          rows="1"
          maxlength="1000"
          @keydown.enter.exact.prevent="handleSubmit"
          @keydown.enter.shift="() => {}"
        />
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          v-if="isLoading"
          type="button"
          class="bg-red-500 text-white px-5 py-4 rounded-xl hover:bg-red-600 transition-all flex items-center gap-2 shadow-md"
          @click="$emit('stop')"
        >
          <span class="text-sm font-medium">停止</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <button
          type="submit"
          :disabled="disabled || isDemoMode || isLimitReached || !canSend"
          class="send-button bg-[#37acdb] text-white px-6 py-4 rounded-xl transition-all flex items-center gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <span v-if="!isLoading" class="text-sm font-medium">送信</span>
          <span v-else class="text-sm font-medium">送信中...</span>
          <svg
            v-if="!isLoading"
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
          <svg
            v-else
            class="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: { type: Boolean, default: false },
  isDemoMode: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  isLimitReached: { type: Boolean, default: false },
  isUnlimited: { type: Boolean, default: false },
  planChangeUrl: { type: String, default: '#' },
});

const emit = defineEmits(['update:modelValue', 'submit', 'stop']);

const textareaRef = ref(null);
const inputValue = ref(props.modelValue);

watch(() => props.modelValue, (v) => { inputValue.value = v; });
watch(inputValue, (v) => { emit('update:modelValue', v); });

const placeholderTrimmed = computed(() => (props.placeholder || '').trim());

const textareaA11yAttrs = computed(() => {
  const a = {};
  if (placeholderTrimmed.value) {
    a.placeholder = placeholderTrimmed.value;
  } else {
    a['aria-label'] = '質問を入力';
  }
  return a;
});

const canSend = computed(() => inputValue.value.trim().length > 0);

function handleSubmit() {
  if (props.isDemoMode || !canSend.value || props.disabled || props.isLimitReached) return;
  emit('submit', inputValue.value.trim());
  inputValue.value = '';
}
</script>

<style scoped>
/* モバイル: viewport底に固定（用語集ボタン分の右スペース確保済み） */
.chat-input-area {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: white;
  border-top: 1px solid #f3f4f6;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
}

/* デスクトップ: チャットコンテナ内の通常フロー */
@media (min-width: 768px) {
  .chat-input-area {
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    box-shadow: none;
    border-top: 1px solid #f3f4f6;
  }
}
</style>
